import React from 'react';
import {
    SearchOutlined,
    PlusOutlined,
    CalendarOutlined,
    TeamOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import ScheduleDetailPanel from '../components/ScheduleDetailPanel';
import CreateScheduleModal from '../components/CreateScheduleModal';
import EditScheduleModal from '../components/EditScheduleModal';
import DeleteScheduleModal from '../components/DeleteScheduleModal';
import { useSchedules } from '../hooks/useSchedules';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    ErrorBlock,
} from '@/pages/inside/tenant/style';

const Schedules = () => {
    const {
        total,
        activeCount,
        totalAssignedEmployees,
        employeeOptions,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        // panel state
        panelOpen,
        openPanel,
        closePanel,
        selectedScheduleId,
        selectedScheduleName,
        // create modal
        canCreate,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createSchedulePending,
        // edit modal
        editModalOpen,
        closeEditModal,
        editForm,
        onSubmitEdit,
        updateSchedulePending,
        // delete confirm
        deleteConfirmOpen,
        setDeleteConfirmOpen,
        confirmDelete,
        deleteSchedulePending,
        scheduleForDelete,
        // error
        isError,
    } = useSchedules();

    return (
        <ListBodyLayout
            title="Schedules"
            description="Manage employee shift schedules"
            actions={
                canCreate && (
                    <Button
                        $primary
                        $blackOnLight
                        icon={<PlusOutlined />}
                        onClick={() => setCreateModalOpen(true)}
                        $padding="10px 20px"
                        $size="14px"
                    >
                        Create Schedule
                    </Button>
                )
            }
            kpiCards={[
                {
                    label: 'Total Schedules',
                    value: total,
                    icon: <CalendarOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'schedules',
                    trendPositive: true,
                },
                {
                    label: 'Active shifts',
                    value: activeCount,
                    icon: <ClockCircleOutlined style={{ color: '#10b981' }} />,
                    trend: 'active shifts',
                    trendPositive: true,
                },
                {
                    label: 'Assigned Employees',
                    value: totalAssignedEmployees,
                    icon: <TeamOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'employees',
                    trendPositive: true,
                },
            ]}
        >
            {isError && (
                <ErrorBlock>
                    Unable to load schedules. Please verify that the Schedules API is running.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.ACTIVE, label: 'Active' },
                                { key: STATUS_TAB.INACTIVE, label: 'Inactive' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search schedules"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
            />

            <AppModal
                size="md"
                title={selectedScheduleName}
                open={panelOpen}
                onCancel={closePanel}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <ScheduleDetailPanel
                    scheduleId={selectedScheduleId}
                    onClose={closePanel}
                />
            </AppModal>

            <CreateScheduleModal
                open={createModalOpen}
                onCancel={closeCreateModal}
                form={createForm}
                onSubmit={onSubmitCreate}
                employeeOptions={employeeOptions}
                loading={createSchedulePending}
            />

            <EditScheduleModal
                open={editModalOpen}
                onCancel={closeEditModal}
                form={editForm}
                onSubmit={onSubmitEdit}
                employeeOptions={employeeOptions}
                loading={updateSchedulePending}
            />

            <DeleteScheduleModal
                open={deleteConfirmOpen}
                onCancel={() => setDeleteConfirmOpen(false)}
                onOk={confirmDelete}
                loading={deleteSchedulePending}
                scheduleForDelete={scheduleForDelete}
            />
        </ListBodyLayout>
    );
};

export default Schedules;
